import { useMemo, useRef, type Dispatch, type SetStateAction } from "react";
import { Label, Input, Select } from "@tellescope/design-system";
import { Combobox, DatePicker } from "@tellescope/ui";
import type { ComboboxOption } from "@tellescope/ui";
import type { TellescopeFormFieldBaseProps, TellescopeFieldResponse } from "../../types";
import { useTranslation } from "react-i18next";
import {
  CANVAS_TITLE,
  INSURANCE_RELATIONSHIPS,
  INSURANCE_RELATIONSHIPS_CANVAS,
  TELLESCOPE_GENDERS,
} from "@tellescope/constants";
import type { DatabaseRecord, Enduser, InsuranceRelationship, TellescopeGender } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { cn } from "@tellescope/utils";
import { FieldDescription } from "../../FieldDescription";
import { useInsurerDropdown } from "./useInsurerDropdown";
import { parseDateStringAnswer, toDateStringAnswer } from "../../utils";

const convertStringToInsuranceRelationship = (str: string): InsuranceRelationship | undefined =>
  INSURANCE_RELATIONSHIPS.find((item) => item === str);

const convertStringToGender = (str: string): TellescopeGender | undefined =>
  TELLESCOPE_GENDERS.find((item) => item === str);

const parsePolicyOwnerDateOfBirth = (dateOfBirth: string | undefined): Date | undefined => {
  if (!dateOfBirth) return undefined;
  const parsed = parseDateStringAnswer(dateOfBirth);
  if (parsed) return new Date(parsed.year, parsed.month - 1, parsed.day);
  const fallback = new Date(dateOfBirth);
  return isNaN(fallback.getTime()) ? undefined : fallback;
};

export interface InsuranceFieldProps extends TellescopeFormFieldBaseProps<"Insurance"> {
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  formResponses?: TellescopeFieldResponse[];
  onDatabaseSelect?: (records: DatabaseRecord[]) => void;
}

export const InsuranceField = ({
  id,
  title,
  description,
  htmlDescription,
  value,
  onChange,
  className,
  onBlur,
  error,
  required,
  options,
  enduser,
  formResponses = [],
  onDatabaseSelect,
  autoFocus,
}: InsuranceFieldProps) => {
  const { t } = useTranslation("forms");
  const valueRef = useRef(value);
  valueRef.current = value;

  const { payers, isLoading, isLiveSearch, searchQuery, setSearchQuery } = useInsurerDropdown({
    fieldId: id,
    options,
    formResponses,
    enduser,
  });

  const requireStrict = options?.requirePredefinedInsurer === true;

  const comboboxOptions = useMemo<ComboboxOption<string>[]>(
    () => payers.map((p) => ({ value: p.id || p.name, label: p.name })),
    [payers]
  );

  const selectedOption = useMemo<ComboboxOption<string> | undefined>(() => {
    const payerName = value?.payerName;
    if (!payerName) return undefined;
    const match = payers.find((p) => p.name === payerName);
    if (match) return { value: match.id || match.name, label: match.name };
    if (!requireStrict) return { value: payerName, label: payerName };
    return undefined;
  }, [value?.payerName, payers, requireStrict]);

  const handleInsurerSelect = (option: ComboboxOption<string> | undefined) => {
    if (!option) {
      onChange({ ...valueRef.current, payerName: "", payerId: "", payerType: undefined });
      return;
    }
    const match = payers.find((p) => (p.id || p.name) === option.value);
    onChange({
      ...valueRef.current,
      payerName: match?.name ?? option.label,
      payerId: match?.id ?? "",
      payerType: match?.type,
    });
    if (match?.databaseRecord) onDatabaseSelect?.([match.databaseRecord]);
  };

  const handleSearchChange: Dispatch<SetStateAction<string>> = (queryOrUpdater) => {
    const currentValue = valueRef.current;
    const previousQuery = currentValue?.payerName ?? "";
    const query = typeof queryOrUpdater === "function" ? queryOrUpdater(previousQuery) : queryOrUpdater;
    setSearchQuery(query);
    if (!requireStrict) {
      const match = payers.find((p) => p.name === query);
      onChange({
        ...currentValue,
        payerName: query,
        payerId: match?.id ?? (currentValue?.payerName === query ? currentValue?.payerId ?? "" : ""),
        payerType: match?.type ?? (currentValue?.payerName === query ? currentValue?.payerType : undefined),
      });
    }
  };

  const relationships = options?.dataSource === CANVAS_TITLE ? INSURANCE_RELATIONSHIPS_CANVAS : INSURANCE_RELATIONSHIPS;
  const relationship = value?.relationship ?? "Self";

  const comboboxSearchQuery = !requireStrict ? value?.payerName ?? "" : isLiveSearch ? searchQuery : undefined;

  const comboboxSetSearchQuery = !requireStrict ? handleSearchChange : isLiveSearch ? setSearchQuery : undefined;

  return (
    <div className="space-y-1">
      {!!title && <Label required={required}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <div className="grid grid-cols-2 gap-2">
        <Combobox
          value={selectedOption}
          onChange={handleInsurerSelect}
          options={comboboxOptions}
          isLoading={isLoading}
          placeholder={isLiveSearch ? t("Search insurer...") : t("Insurer")}
          searchPlaceholder={t("Search...")}
          clearable={!requireStrict}
          searchQuery={comboboxSearchQuery}
          setSearchQuery={comboboxSetSearchQuery}
          customFilter={isLiveSearch ? () => comboboxOptions : undefined}
          emptyText={isLiveSearch && !searchQuery ? t("Type to search for options") : t("No results found")}
          autoFocus={autoFocus}
        />
        <Input
          value={value?.memberId ?? ""}
          onChange={(e) => onChange({ ...valueRef.current, memberId: e.target.value })}
          onBlur={onBlur}
          placeholder={t("Member ID")}
        />
        <Input
          value={value?.planName ?? ""}
          onChange={(e) => onChange({ ...valueRef.current, planName: e.target.value })}
          onBlur={onBlur}
          placeholder={t("Plan Name")}
        />
        <DatePicker
          value={value?.startDate ? new Date(value.startDate) : undefined}
          onChange={(date) => onChange({ ...valueRef.current, startDate: date?.toISOString() })}
          onBlur={onBlur}
          placeholder={t("Plan Start Date")}
          label=""
          className="w-full"
        />
        {options?.includeGroupNumber && (
          <Input
            value={value?.groupNumber ?? ""}
            onChange={(e) => onChange({ ...valueRef.current, groupNumber: e.target.value })}
            onBlur={onBlur}
            placeholder={t("Group Number")}
          />
        )}
        <Select
          value={relationship}
          onValueChange={(nextRelationship) =>
            onChange({
              ...valueRef.current,
              relationship: convertStringToInsuranceRelationship(nextRelationship),
            })
          }
        >
          <Select.Trigger className={cn("col-span-2 w-full", className)}>
            <Select.Value placeholder={t("Relationship to Policy Owner")} />
          </Select.Trigger>
          <Select.Content searchPlaceholder={t("Search...")}>
            {relationships.map((relationshipOption) => (
              <Select.Item key={relationshipOption} value={relationshipOption}>
                {relationshipOption}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
        {relationship !== "Self" && (
          <>
            <Input
              value={value?.relationshipDetails?.fname ?? ""}
              onChange={(e) =>
                onChange({
                  ...valueRef.current,
                  relationshipDetails: {
                    ...valueRef.current?.relationshipDetails,
                    fname: e.target.value,
                  },
                })
              }
              onBlur={onBlur}
              placeholder={t("Policy Owner First Name")}
            />
            <Input
              value={value?.relationshipDetails?.lname ?? ""}
              onChange={(e) =>
                onChange({
                  ...valueRef.current,
                  relationshipDetails: {
                    ...valueRef.current?.relationshipDetails,
                    lname: e.target.value,
                  },
                })
              }
              onBlur={onBlur}
              placeholder={t("Policy Owner Last Name")}
            />
            <Select
              value={value?.relationshipDetails?.gender}
              onValueChange={(gender) =>
                onChange({
                  ...valueRef.current,
                  relationshipDetails: {
                    ...valueRef.current?.relationshipDetails,
                    gender: convertStringToGender(gender),
                  },
                })
              }
            >
              <Select.Trigger className="col-span-2 w-full">
                <Select.Value placeholder={t("Policy Owner Gender")} />
              </Select.Trigger>
              <Select.Content searchPlaceholder={t("Search...")}>
                {TELLESCOPE_GENDERS.map((gender) => (
                  <Select.Item key={gender} value={gender}>
                    {gender}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
            <DatePicker
              value={parsePolicyOwnerDateOfBirth(value?.relationshipDetails?.dateOfBirth)}
              onChange={(date) =>
                onChange({
                  ...valueRef.current,
                  relationshipDetails: {
                    ...valueRef.current?.relationshipDetails,
                    dateOfBirth: date ? toDateStringAnswer(date) : undefined,
                  },
                })
              }
              onBlur={onBlur}
              placeholder={t("Policy Owner Date of Birth")}
              label=""
              className="w-full col-span-2"
            />
          </>
        )}
      </div>
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
